import React, { useMemo, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import Select from 'react-select';
import axiosInstance from '../../utils/axiosInstance';

export default function ElmsModuleCode() {
  const [codes, setCodes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [totalCodes, setTotalCodes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    subject_id: 0,
    module_code: '',
    elms_module_code: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const API_BASE = '/admin/elms-module-codes';

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axiosInstance.get(API_BASE);
      const data = resp.data || {};
      setCodes(Array.isArray(data.codes) ? data.codes : []);
      setSubjects(Array.isArray(data.subjects) ? data.subjects : []);
      setTotalCodes(typeof data.totalCodes === 'number' ? data.totalCodes : (data.codes ? data.codes.length : 0));
    } catch (e) {
      console.error('Failed to load ELMS module codes', e);
      setError(e.response?.data?.error || 'Failed to load ELMS module codes.');
      setCodes([]);
      setSubjects([]);
      setTotalCodes(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return codes;
    const q = search.toLowerCase();
    return codes.filter(c =>
      c.elms_module_code.toLowerCase().includes(q) ||
      c.module_code.toLowerCase().includes(q) ||
      (c.subject?.fullName?.toLowerCase().includes(q) || c.subject?.shortName?.toLowerCase().includes(q))
    );
  }, [codes, search]);

  const subjectOptions = useMemo(() => {
    return subjects.map(subject => ({
      value: subject.subjectId,
      label: `${subject.fullName} (${subject.shortName})`
    }));
  }, [subjects]);

  const subjectSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '0.5rem',
      borderWidth: '1px',
      borderColor: errors.subject_id 
        ? '#ef4444' 
        : state.isFocused 
        ? '#6366f1' 
        : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
      '&:hover': {
        borderColor: errors.subject_id ? '#ef4444' : '#a5b4fc',
      },
      minHeight: '42px',
      paddingLeft: '4px',
      paddingRight: '4px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#6366f1' 
        : state.isFocused 
        ? '#e0e7ff' 
        : 'white',
      color: state.isSelected ? 'white' : '#111827',
      fontWeight: state.isSelected ? '500' : '400',
      '&:hover': {
        backgroundColor: state.isSelected ? '#6366f1' : '#e0e7ff',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '4px',
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (base) => ({
      ...base,
      fontWeight: '500',
      fontSize: '0.875rem',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '0.875rem',
      color: '#9ca3af',
    }),
  };

  const resetForm = () => {
    setForm({ subject_id: 0, module_code: '', elms_module_code: '' });
    setErrors({});
  };

  const openAdd = () => { resetForm(); setShowAdd(true); };
  const openEdit = (row) => {
    setSelected(row);
    setForm({ subject_id: row.subject_id, module_code: row.module_code, elms_module_code: row.elms_module_code });
    setErrors({});
    setShowEdit(true);
  };

  const validate = async (isEdit = false) => {
    const e = {};
    if (!form.subject_id) e.subject_id = 'Subject is required';
    if (!form.module_code.trim()) e.module_code = 'Module code is required';
    if (!form.elms_module_code.trim()) e.elms_module_code = 'ELMS module code is required';
    if (form.module_code.length > 255) e.module_code = 'Max 255 characters';
    if (form.elms_module_code.length > 255) e.elms_module_code = 'Max 255 characters';

    const canCheckUniqueness = !!form.subject_id;
    if (!e.module_code && canCheckUniqueness) {
      const params = {
        value: form.module_code,
        field: 'module_code',
        subject_id: String(form.subject_id),
      };
      if (isEdit && selected) params.excludeId = String(selected.elms_module_code_id);
      try {
        const resp = await axiosInstance.get(`${API_BASE}/check`, { params });
        if (resp.data?.exists) {
          e.module_code = 'This module code already exists for the selected subject';
        }
      } catch (err) {
        console.error('Failed to check module code uniqueness', err);
      }
    }

    if (!e.elms_module_code && canCheckUniqueness) {
      const params = {
        value: form.elms_module_code,
        field: 'elms_module_code',
        subject_id: String(form.subject_id),
      };
      if (isEdit && selected) params.excludeId = String(selected.elms_module_code_id);
      try {
        const resp = await axiosInstance.get(`${API_BASE}/check`, { params });
        if (resp.data?.exists) {
          e.elms_module_code = 'This ELMS module code already exists for the selected subject';
        }
      } catch (err) {
        console.error('Failed to check ELMS module code uniqueness', err);
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitAdd = async (ev) => {
    ev.preventDefault();
    if (!(await validate(false))) return;
    setSubmitting(true);
    try {
      const resp = await axiosInstance.post(API_BASE, form);
      if (resp.data?.success) {
        Swal.fire('Success', resp.data.message || 'ELMS module code added successfully.', 'success');
        setShowAdd(false);
        resetForm();
        loadData();
      } else {
        const msg = resp.data?.error || resp.data?.message || 'Failed to add ELMS module code.';
        Swal.fire('Error', msg, 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to add ELMS module code.';
      Swal.fire('Error', msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async (ev) => {
    ev.preventDefault();
    if (!selected) return;
    if (!(await validate(true))) return;
    setSubmitting(true);
    try {
      const resp = await axiosInstance.put(`${API_BASE}/${selected.elms_module_code_id}`, form);
      if (resp.data?.success) {
        Swal.fire('Success', resp.data.message || 'ELMS module code updated successfully.', 'success');
        setShowEdit(false);
        setSelected(null);
        resetForm();
        loadData();
      } else {
        const msg = resp.data?.error || resp.data?.message || 'Failed to update ELMS module code.';
        Swal.fire('Error', msg, 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to update ELMS module code.';
      Swal.fire('Error', msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = (row) => {
    Swal.fire({
      title: 'Delete this record?',
      html: `<p><strong>${row.elms_module_code}</strong> (${row.module_code})</p>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444',
    }).then(res => {
      if (res.isConfirmed) {
        axiosInstance.delete(`${API_BASE}/${row.elms_module_code_id}`)
          .then((resp) => {
            if (resp.data?.success) {
              Swal.fire('Deleted', resp.data.message || 'ELMS module code deleted.', 'success');
              loadData();
            } else {
              const msg = resp.data?.error || resp.data?.message || 'Failed to delete ELMS module code.';
              Swal.fire('Error', msg, 'error');
            }
          })
          .catch((err) => {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to delete ELMS module code.';
            Swal.fire('Error', msg, 'error');
          });
      }
    });
  };

  return (
    <DashboardLayout title="ELMS Module Code">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ELMS Module Code</h1>
            <p className="text-sm text-gray-600">Manage ELMS module code mappings per subject</p>
          </div>
          <button onClick={openAdd} className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Add</button>
        </div>

        {/* Stats + Search */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-600">Total Codes</p>
            <p className="text-2xl font-bold">{totalCodes}</p>
          </div>
          <div className="md:col-span-2 rounded-lg border border-gray-200 bg-white p-4">
            <div className="relative">
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by ELMS/module/subject..." className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500" />
              <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow-sm border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Module Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ELMS Module Code</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filtered.length ? filtered.map((row, idx) => (
                <tr key={row.elms_module_code_id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm">{idx + 1}</td>
                  <td className="px-6 py-3 text-sm">
                    {row.subject ? (
                      <div>
                        <div className="font-medium text-gray-900">{row.subject.fullName}</div>
                        <div className="text-gray-500 text-xs">{row.subject.shortName}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500">Subject #{row.subject_id}</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm font-medium">{row.module_code}</td>
                  <td className="px-6 py-3 text-sm">{row.elms_module_code}</td>
                  <td className="px-6 py-3 text-sm">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(row)} className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 hover:bg-blue-200">Edit</button>
                      <button onClick={() => remove(row)} className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 hover:bg-red-200">Delete</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td className="px-6 py-8 text-center text-gray-500" colSpan={5}>No records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold mb-4">Add ELMS Module Code</h2>
            <form onSubmit={submitAdd}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Subject <span className="text-red-500">*</span></label>
                  <Select
                    options={subjectOptions}
                    value={subjectOptions.find(opt => opt.value === form.subject_id) || null}
                    onChange={(selected) => setForm(f => ({ ...f, subject_id: selected ? selected.value : 0 }))}
                    styles={subjectSelectStyles}
                    placeholder="Select subject..."
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.subject_id && <p className="mt-1 text-sm text-red-600">{errors.subject_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Module Code <span className="text-red-500">*</span></label>
                  <input value={form.module_code} onChange={e=>setForm(f=>({...f, module_code: e.target.value}))} className={`w-full rounded-lg border ${errors.module_code? 'border-red-500' : 'border-gray-300'} px-3 py-2`} />
                  {errors.module_code && <p className="mt-1 text-sm text-red-600">{errors.module_code}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ELMS Module Code <span className="text-red-500">*</span></label>
                  <input value={form.elms_module_code} onChange={e=>setForm(f=>({...f, elms_module_code: e.target.value}))} className={`w-full rounded-lg border ${errors.elms_module_code? 'border-red-500' : 'border-gray-300'} px-3 py-2`} />
                  {errors.elms_module_code && <p className="mt-1 text-sm text-red-600">{errors.elms_module_code}</p>}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => { setShowAdd(false); resetForm(); }} className="rounded-lg border px-4 py-2">Cancel</button>
                <button type="submit" disabled={submitting} className="rounded-lg bg-indigo-600 text-white px-4 py-2 disabled:opacity-50">{submitting? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold mb-4">Edit ELMS Module Code</h2>
            <form onSubmit={submitEdit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Subject <span className="text-red-500">*</span></label>
                  <Select
                    options={subjectOptions}
                    value={subjectOptions.find(opt => opt.value === form.subject_id) || null}
                    onChange={(selected) => setForm(f => ({ ...f, subject_id: selected ? selected.value : 0 }))}
                    styles={subjectSelectStyles}
                    placeholder="Select subject..."
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.subject_id && <p className="mt-1 text-sm text-red-600">{errors.subject_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Module Code <span className="text-red-500">*</span></label>
                  <input value={form.module_code} onChange={e=>setForm(f=>({...f, module_code: e.target.value}))} className={`w-full rounded-lg border ${errors.module_code? 'border-red-500' : 'border-gray-300'} px-3 py-2`} />
                  {errors.module_code && <p className="mt-1 text-sm text-red-600">{errors.module_code}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ELMS Module Code <span className="text-red-500">*</span></label>
                  <input value={form.elms_module_code} onChange={e=>setForm(f=>({...f, elms_module_code: e.target.value}))} className={`w-full rounded-lg border ${errors.elms_module_code? 'border-red-500' : 'border-gray-300'} px-3 py-2`} />
                  {errors.elms_module_code && <p className="mt-1 text-sm text-red-600">{errors.elms_module_code}</p>}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => { setShowEdit(false); setSelected(null); resetForm(); }} className="rounded-lg border px-4 py-2">Cancel</button>
                <button type="submit" disabled={submitting} className="rounded-lg bg-indigo-600 text-white px-4 py-2 disabled:opacity-50">{submitting? 'Saving...' : 'Save changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
